import { Type } from 'typebox';
import { Value } from 'typebox/value';
import { HealthScreeningQuestion, ScreeningTypeCodec } from '../health/health-screening.js';
export const FeatureValue = Type.Cyclic({
    FeatureValue: Type.Union([
        Type.Null(),
        Type.Boolean(),
        Type.Number(),
        Type.String(),
        Type.Array(Type.Ref('FeatureValue')),
        Type.Record(Type.String(), Type.Ref('FeatureValue'))
    ])
}, 'FeatureValue');
export const Feature = Type.Object({
    enabled: Type.Boolean(),
    identifier: Type.String({ minLength: 1 }),
    value: Type.Optional(FeatureValue)
});
export const FeatureSet = Type.Object({
    scope: Type.String({ minLength: 1 }),
    key: Type.String({ minLength: 1 }),
    features: Type.Array(Feature)
});
export const TsgIntegrationModes = {
    gold: 'gold',
    goldAndTadpoles: 'gold+tadpoles',
    tadpoles: 'tadpoles'
};
export const TsgIntegrationMode = Type.Union([
    Type.Literal(TsgIntegrationModes.gold),
    Type.Literal(TsgIntegrationModes.goldAndTadpoles),
    Type.Literal(TsgIntegrationModes.tadpoles)
], {
    default: TsgIntegrationModes.tadpoles
});
export const LanguageVariants = {
    none: 'None',
    la: 'LA'
};
export const LanguageVariant = Type.Union([Type.Literal(LanguageVariants.none), Type.Literal(LanguageVariants.la)], {
    default: LanguageVariants.none
});
export const HealthCheckQuestionnaire = Type.Object({
    id: Type.String({ minLength: 1 }),
    locationKey: Type.String({ minLength: 1 }),
    name: Type.String(),
    questions: Type.Array(HealthScreeningQuestion),
    type: ScreeningTypeCodec
});
const DefaultHealthCheckConfig = {
    allowChildScreeningAdminOverride: false,
    allowStaffScreeningAdminOverride: false,
    celsius: false,
    maximumTemperature: '100.3',
    notifyChildScreening: false,
    notifyChildScreeningDashboard: false,
    notifyChildScreeningEmail: false,
    notifyChildScreeningProviderApp: false,
    notifyChildScreeningText: false,
    notifyStaffScreening: false,
    notifyStaffScreeningDashboard: false,
    notifyStaffScreeningEmail: false,
    notifyStaffScreeningProviderApp: false,
    notifyStaffScreeningText: false,
    parentChildScreeningWarning: false,
    requireChildScreening: false,
    requireStaffScreening: false,
    staffChildScreeningWarning: false,
    staffStaffScreeningWarning: false
};
export const HealthCheckConfig = Type.Object({
    allowChildScreeningAdminOverride: Type.Boolean(),
    allowStaffScreeningAdminOverride: Type.Boolean(),
    celsius: Type.Boolean(),
    dropOffQuestionnaire: Type.Optional(HealthCheckQuestionnaire),
    employeeQuestionnaire: Type.Optional(HealthCheckQuestionnaire),
    maximumTemperature: Type.String(),
    notifyChildScreening: Type.Boolean(),
    notifyChildScreeningDashboard: Type.Boolean(),
    notifyChildScreeningEmail: Type.Boolean(),
    notifyChildScreeningProviderApp: Type.Boolean(),
    notifyChildScreeningText: Type.Boolean(),
    notifyStaffScreening: Type.Boolean(),
    notifyStaffScreeningDashboard: Type.Boolean(),
    notifyStaffScreeningEmail: Type.Boolean(),
    notifyStaffScreeningProviderApp: Type.Boolean(),
    notifyStaffScreeningText: Type.Boolean(),
    parentChildScreeningWarning: Type.Boolean(),
    parentChildScreeningWarningText: Type.Optional(Type.String()),
    requireChildScreening: Type.Boolean(),
    requireStaffScreening: Type.Boolean(),
    screeningWarningDefaultText: Type.Optional(Type.String()),
    staffChildScreeningWarning: Type.Boolean(),
    staffChildScreeningWarningText: Type.Optional(Type.String()),
    staffStaffScreeningWarning: Type.Boolean(),
    staffStaffScreeningWarningText: Type.Optional(Type.String())
}, {
    default: DefaultHealthCheckConfig
});
export const ChildFeatures = Type.Object({
    parent_dropoff_time_limit: Type.Number({ default: 30 }),
    tsg: Type.Boolean({ default: false }),
    no_goal_sharing: Type.Boolean({ default: true }),
    disable_parent_notes: Type.Boolean({ default: true }),
    parent_intraday_dailies: Type.Boolean({ default: true }),
    translation: Type.Record(Type.String(), Type.String(), { default: {} }),
    tsg_mode: TsgIntegrationMode,
    enable_parent_observation_sharing: Type.Boolean({ default: true }),
    enable_unexpected_absence_notifications: Type.Boolean({ default: true }),
    share_daily_report_sleep_checks: Type.Boolean({ default: true }),
    hide_food_provided_parent_option: Type.Boolean({ default: true }),
    allow_chronological_daily_report: Type.Boolean({ default: true }),
    enable_family_messaging: Type.Boolean({ default: true }),
    library_videos: Type.Boolean({ default: false }),
    library_ebooks: Type.Boolean({ default: false }),
    disable_family_messaging_attachments: Type.Boolean({ default: true }),
    disable_billing: Type.Boolean({ default: true }),
    disable_family_playlist: Type.Boolean({ default: true }),
    disable_health_checks: Type.Boolean({ default: true }),
    health_check: HealthCheckConfig,
    start_of_week: Type.Integer({ default: 1 }),
    disable_family_personal_info_edit: Type.Boolean({ default: true }),
    enable_activity_duration_tracking: Type.Boolean({ default: true }),
    language_variant: LanguageVariant
});
export function createDefaultHealthCheckConfig() {
    return Value.Decode(HealthCheckConfig, undefined);
}
export function createDefaultChildFeatures() {
    return Value.Decode(ChildFeatures, {});
}
export function decodeChildFeatures(features, options = {}) {
    const result = createDefaultChildFeatures();
    for (const feature of features) {
        const decoded = feature.enabled ? result : createDefaultChildFeatures();
        const reportInvalidValue = () => options.onInvalidValue?.(feature);
        switch (feature.identifier) {
            case 'parent_dropoff_time_limit':
                decoded.parent_dropoff_time_limit = decodeNumber(feature.value, decoded.parent_dropoff_time_limit, reportInvalidValue);
                break;
            case 'tsg':
                decoded.tsg = decodeBoolean(feature.value, decoded.tsg, reportInvalidValue);
                break;
            case 'no_goal_sharing':
                decoded.no_goal_sharing = decodeBoolean(feature.value, decoded.no_goal_sharing, reportInvalidValue);
                break;
            case 'disable_parent_notes':
                decoded.disable_parent_notes = decodeBoolean(feature.value, decoded.disable_parent_notes, reportInvalidValue);
                break;
            case 'parent_intraday_dailies':
                decoded.parent_intraday_dailies = decodeBoolean(feature.value, decoded.parent_intraday_dailies, reportInvalidValue);
                break;
            case 'translation':
                decoded.translation = decodeOrDefault(ChildFeatures.properties.translation, feature.value, decoded.translation, reportInvalidValue);
                break;
            case 'tsg_mode':
                decoded.tsg_mode = decodeOrDefault(TsgIntegrationMode, feature.value, decoded.tsg_mode, reportInvalidValue);
                break;
            case 'enable_parent_observation_sharing':
                decoded.enable_parent_observation_sharing = decodeBoolean(feature.value, decoded.enable_parent_observation_sharing, reportInvalidValue);
                break;
            case 'enable_unexpected_absence_notifications':
                decoded.enable_unexpected_absence_notifications = decodeBoolean(feature.value, decoded.enable_unexpected_absence_notifications, reportInvalidValue);
                break;
            case 'share_daily_report_sleep_checks':
                decoded.share_daily_report_sleep_checks = decodeBoolean(feature.value, decoded.share_daily_report_sleep_checks, reportInvalidValue);
                break;
            case 'hide_food_provided_parent_option':
                decoded.hide_food_provided_parent_option = decodeBoolean(feature.value, decoded.hide_food_provided_parent_option, reportInvalidValue);
                break;
            case 'allow_chronological_daily_report':
                decoded.allow_chronological_daily_report = decodeBoolean(feature.value, decoded.allow_chronological_daily_report, reportInvalidValue);
                break;
            case 'enable_family_messaging':
                decoded.enable_family_messaging = decodeBoolean(feature.value, decoded.enable_family_messaging, reportInvalidValue);
                break;
            case 'library_videos':
                decoded.library_videos = decodeBoolean(feature.value, decoded.library_videos, reportInvalidValue);
                break;
            case 'library_ebooks':
                decoded.library_ebooks = decodeBoolean(feature.value, decoded.library_ebooks, reportInvalidValue);
                break;
            case 'disable_family_messaging_attachments':
                decoded.disable_family_messaging_attachments = decodeBoolean(feature.value, decoded.disable_family_messaging_attachments, reportInvalidValue);
                break;
            case 'disable_billing':
                decoded.disable_billing = decodeBoolean(feature.value, decoded.disable_billing, reportInvalidValue);
                break;
            case 'disable_family_playlist':
                decoded.disable_family_playlist = decodeBoolean(feature.value, decoded.disable_family_playlist, reportInvalidValue);
                break;
            case 'disable_health_checks':
                decoded.disable_health_checks = decodeBoolean(feature.value, decoded.disable_health_checks, reportInvalidValue);
                break;
            case 'health_check':
                decoded.health_check = decodeOrDefault(HealthCheckConfig, feature.value, decoded.health_check, reportInvalidValue);
                break;
            case 'start_of_week':
                decoded.start_of_week = decodeInteger(feature.value, decoded.start_of_week, reportInvalidValue);
                break;
            case 'disable_family_personal_info_edit':
                decoded.disable_family_personal_info_edit = decodeBoolean(feature.value, decoded.disable_family_personal_info_edit, reportInvalidValue);
                break;
            case 'enable_activity_duration_tracking':
                decoded.enable_activity_duration_tracking = decodeBoolean(feature.value, decoded.enable_activity_duration_tracking, reportInvalidValue);
                break;
            case 'language_variant':
                decoded.language_variant = decodeOrDefault(LanguageVariant, feature.value, decoded.language_variant, reportInvalidValue);
                break;
        }
    }
    return result;
}
function decodeBoolean(value, defaultValue, reportInvalidValue) {
    return decodeOrDefault(Type.Boolean(), value, defaultValue, reportInvalidValue);
}
function decodeNumber(value, defaultValue, reportInvalidValue) {
    const decoded = decodeNumericString(value);
    if (typeof decoded === 'number') {
        return decoded;
    }
    reportInvalidValue();
    return defaultValue;
}
function decodeInteger(value, defaultValue, reportInvalidValue) {
    const decoded = decodeNumericString(value);
    if (typeof decoded === 'number' && Number.isInteger(decoded)) {
        return decoded;
    }
    reportInvalidValue();
    return defaultValue;
}
function decodeNumericString(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value !== 'string' || value.trim().length === 0) {
        return undefined;
    }
    const decoded = Number(value);
    return Number.isFinite(decoded) ? decoded : undefined;
}
function decodeOrDefault(schema, value, defaultValue, reportInvalidValue) {
    if (Value.Check(schema, value)) {
        return Value.Decode(schema, value);
    }
    reportInvalidValue();
    return defaultValue;
}
